import {
  HalfFloatType,
  NearestFilter,
  NodeMaterial,
  QuadMesh,
  RenderTarget,
  type Node,
  type Renderer,
  type Texture,
} from 'three/webgpu';
import { Fn, Loop, float, luminance, max, min, mix, nodeObject, texture, uniform, vec2, vec4 } from 'three/tsl';

// The frame is metered on a grid of cells rather than per pixel, and each cell
// is averaged from four taps, so a single grain speck or specular hit can't set
// the black or white point on its own.
const CELLS = 16;

// Only the middle of the frame is metered. The vignette pins the corners near
// black, and metering them would peg the black point at zero every frame.
const METERED = 0.8;

// Seconds for the levels to travel ~63% of the way to a new reading. Long
// enough that a passing highlight doesn't pump the whole image.
const ADAPTATION = 0.4;

// The narrowest black-to-white span the correction will accept, which caps the
// contrast gain at 1 / MIN_RANGE. Without it a frame with almost nothing in it —
// the first moments of the title card burning on — would be stretched straight
// back into a full-contrast image, pumping the empty background up to a glow.
const MIN_RANGE = 0.65;

export interface AutoLevels {
  /** The corrected colour, ready to be used as the pipeline's output node. */
  node: Node<'vec4'>;
  /** `0` leaves the frame alone, `1` applies the whole stretch. */
  strength: { value: number };
  /**
   * Meters the frame sitting in the source target. Call once per frame, before
   * the pipeline renders — it reads the frame the pipeline left there last time
   * round, which is a frame of latency the smoothing hides anyway.
   */
  measure(renderer: Renderer, delta: number): void;
  dispose(): void;
}

/**
 * An auto-levels pass: it measures the darkest and brightest parts of the
 * finished frame and stretches what it finds back out to fill the display
 * range, the way the levels black and white points would be dragged in by hand.
 *
 * The measurement is a single 1x1 render, so the reading lives in a texture
 * rather than on the CPU and never stalls the frame waiting for a read back.
 * Two targets are used: the pass smooths the new reading against the previous
 * one, so it reads from the target it isn't writing and the two swap each time.
 *
 * `source` is the finished frame as a node — leaving it as a node rather than a
 * texture is what keeps the pass that produced it in the render graph — and
 * `frameTexture` is the render target that frame lands in, which the metering
 * pass samples directly so it can read the frame at arbitrary points.
 */
export function autoLevels(source: Node<'vec4'>, frameTexture: Texture, strength = 1): AutoLevels {
  const options = {
    depthBuffer: false,
    type: HalfFloatType,
    minFilter: NearestFilter,
    magFilter: NearestFilter,
  };

  // `front` always holds the newest reading.
  let front = new RenderTarget(1, 1, options);
  let back = new RenderTarget(1, 1, options);
  front.texture.name = 'AutoLevels.levels';
  back.texture.name = 'AutoLevels.levels';

  const levels = texture(front.texture);
  const history = texture(back.texture);
  const frame = texture(frameTexture);

  const rate = uniform(1);
  const amount = uniform(strength);
  const centre = vec2(0.5, 0.5);

  const material = new NodeMaterial();
  material.name = 'AutoLevels.measure';
  material.fragmentNode = Fn(() => {
    const black = float(1).toVar();
    const white = float(0).toVar();

    const cell = METERED / CELLS;
    const origin = (1 - METERED) / 2;

    Loop({ type: 'int', start: 0, end: CELLS }, { type: 'int', start: 0, end: CELLS }, ({ i, j }) => {
      const corner = vec2(float(i), float(j)).mul(cell).add(origin);
      const tap = (x: number, y: number) =>
        luminance(nodeObject(frame.sample(corner.add(vec2(cell * x, cell * y)))).rgb);

      const level = tap(0.25, 0.25)
        .add(tap(0.75, 0.25))
        .add(tap(0.25, 0.75))
        .add(tap(0.75, 0.75))
        .mul(0.25);

      black.assign(min(black, level));
      white.assign(max(white, level));
    });

    // Alpha marks a reading that has actually been taken. The first pass reads
    // a cleared target, and blending against that would drag the levels up from
    // black over the opening second, so the first reading is taken whole.
    const previous = nodeObject(history.sample(centre)).toVar();
    const blend = mix(float(1), rate, previous.a);

    return vec4(mix(previous.x, black, blend), mix(previous.y, white, blend), 0, 1);
  })();

  const quad = new QuadMesh(material);

  const node = Fn(() => {
    const colour = nodeObject(source).toVar();
    const reading = nodeObject(levels.sample(centre)).toVar();

    // Holding the pair at least MIN_RANGE apart is what caps the gain, and
    // alpha folds the correction out entirely until a reading exists.
    const black = reading.x.clamp(0, 1 - MIN_RANGE).toVar();
    const white = reading.y.clamp(black.add(MIN_RANGE), 1);

    const stretched = colour.rgb.sub(black).div(white.sub(black)).clamp(0, 1);

    return vec4(mix(colour.rgb, stretched, amount.mul(reading.a)), colour.a);
  })();

  let started = false;

  return {
    node,
    strength: amount,
    measure(renderer, delta) {
      // The first call lands before anything has been rendered, so there is no
      // frame in the source target to meter yet.
      if (started === false) {
        started = true;
        return;
      }

      rate.value = 1 - Math.exp(-Math.min(delta, 1) / ADAPTATION);
      history.value = front.texture;

      const current = renderer.getRenderTarget();
      renderer.setRenderTarget(back);
      quad.render(renderer);
      renderer.setRenderTarget(current);

      const swap = front;
      front = back;
      back = swap;

      levels.value = front.texture;
    },
    dispose() {
      front.dispose();
      back.dispose();
      material.dispose();
    },
  };
}
